// Follows "HR Design rules.md": GEN, DIS
import * as React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { toggleVariants } from './toggle';

/**
 * A row of joined buttons where one (type="single") or several (type="multiple") stay pressed: the
 * List / Calendar switch, date presets, the table density switch (TBL-11) and a queue's status filter
 * (Pending, Approved, ...) with a count badge in each item. Outline items join into one segmented control.
 * Arrow keys move between the items.
 */
type ToggleGroupStyle = VariantProps<typeof toggleVariants>;

const ToggleGroupContext = React.createContext<ToggleGroupStyle>({});

function ToggleGroup({
  className,
  variant,
  size,
  children,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root> & ToggleGroupStyle) {
  const style = React.useMemo(() => ({ variant, size }), [variant, size]);
  return (
    <ToggleGroupPrimitive.Root
      data-slot="toggle-group"
      data-variant={variant ?? 'default'}
      data-size={size ?? 'default'}
      className={cn(
        'group/toggle-group flex w-fit items-center rounded-md',
        variant === 'outline' ? 'shadow-sm' : 'gap-1',
        className
      )}
      {...props}
    >
      <ToggleGroupContext.Provider value={style}>{children}</ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  );
}

/** An item takes the group's variant and size unless it sets its own. */
function ToggleGroupItem({
  className,
  variant,
  size,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item> & ToggleGroupStyle) {
  const group = React.useContext(ToggleGroupContext);
  const itemVariant = variant ?? group.variant;
  return (
    <ToggleGroupPrimitive.Item
      data-slot="toggle-group-item"
      data-variant={itemVariant ?? 'default'}
      className={cn(
        toggleVariants({ variant: itemVariant, size: size ?? group.size }),
        itemVariant === 'outline' &&
          'shrink-0 rounded-none shadow-none first:rounded-l-md last:rounded-r-md focus-visible:z-raised [&:not(:first-child)]:border-l-0',
        className
      )}
      {...props}
    />
  );
}

/** The item styles (kept under the old name for callers that style a link like an item). */
const toggleGroupItemVariants = toggleVariants;

export { ToggleGroup, ToggleGroupItem, toggleGroupItemVariants };
